<template>
  <div v-if="show" class="modal-overlay" @click="closeOnOverlayClick">
    <div
      class="modal-content"
      :class="{ 'modal-content--mobile': isMobile }"
      @click.stop
    >
      <button
        v-if="closeButton"
        class="modal-close-button"
        type="button"
        @click="closeModal"
      >
        <svg
          class="h-3 w-3"
          viewBox="0 0 14 14"
          xmlns="http://www.w3.org/2000/svg"
          fill="currentColor"
        >
          <path
            clip-rule="evenodd"
            d="M8.41155 7.00032L13.6886 12.2822C14.079 12.6729 14.0787 13.306 13.688 13.6964C13.2973 14.0867 12.6641 14.0864 12.2738 13.6957L6.99628 8.41348L1.70658 13.6953C1.31577 14.0856 0.682602 14.0851 0.292368 13.6943C-0.0978661 13.3035 -0.0973954 12.6703 0.293419 12.2801L5.58271 6.99863L0.29565 1.70679C-0.094698 1.31609 -0.0944116 0.682921 0.296289 0.292573C0.68699 -0.0977741 1.32016 -0.0974878 1.7105 0.293213L6.99797 5.58547L12.2739 0.317343C12.6648 -0.0728905 13.2979 -0.0724199 13.6881 0.318395C14.0784 0.709209 14.0779 1.34237 13.6871 1.73261L8.41155 7.00032Z"
          />
        </svg>
      </button>

      <content-skeleton v-if="loading" />

      <content-invoice
        v-else
        :show="show"
        :prefill-data="prefillData"
        @onSubmit="onSubmit"
      />
    </div>
  </div>
</template>

<script setup>
import {
  computed,
  onMounted,
  onUnmounted,
  watch,
  nextTick,
  ref,
  provide,
} from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'

import ContentInvoice from '@/widgets/ModalInvoice/ui/components/ContentInvoice.vue'
import ContentSkeleton from '@/widgets/ModalInvoice/ui/components/ContentSkeleton.vue'

import {
  INVOICES_GETTERS,
  INVOICES_DISPATCHERS,
} from '@/shared/constants/store-constants/invoices'
import { useKybGuard } from '@/shared/composables/useKybGuard'

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  prefillData: {
    type: Object,
    default: null,
  },
  operationType: {
    type: String,
    default: 'create', // 'create' | 'duplicate' | 'remaining' | 'expired'
  },
  closeButton: {
    type: Boolean,
    default: true,
  },
  closeOutside: {
    type: Boolean,
    default: true,
  },
})

const emits = defineEmits(['update:modelValue', 'onSubmit'])

const store = useStore()
const router = useRouter()
const { ensureKyb } = useKybGuard()

const isFileDialogOpen = ref(false)
provide('isFileDialogOpen', isFileDialogOpen)

const show = computed({
  get() {
    return props.modelValue
  },
  set(value) {
    emits('update:modelValue', value)
  },
})

const loading = computed(() => store.getters[INVOICES_GETTERS.actionLoading])

const isMobile = computed(() => {
  if (typeof window !== 'undefined') {
    return window.innerWidth < 768
  }
  return false
})

function closeModal() {
  show.value = false
}

function closeOnOverlayClick() {
  if (props.closeOutside && !isFileDialogOpen.value) {
    closeModal()
  }
}

function handleBodyScroll() {
  if (show.value) {
    document.body.style.overflow = 'hidden'
  } else {
    document.body.style.overflow = ''
  }
}

onMounted(() => {
  if (show.value) {
    handleBodyScroll()
  }
})

onUnmounted(() => {
  document.body.style.overflow = ''
})

async function onSubmit(formData) {
  if (!ensureKyb()) return

  const apiData = {
    data: {
      amount: parseFloat(formData.amount),
      buyer_email: formData.clientEmail,
      buyer_name: formData.clientName,
      coin_id: formData.currency || 'usdt',
      description: formData.description,
      network_id: 'trc20',
      title: formData.description,
    },
    external_id: generateExternalId(),
    invoice_duration: calculateInvoiceDuration(formData),
  }

  const result = await store.dispatch(INVOICES_DISPATCHERS.createInvoice, {
    formData: apiData,
    operationType: props.operationType,
  })

  if (result.success) {
    closeModal()
    const newId = result?.payload?.id || result?.payload?.data?.id

    if (newId) {
      let notificationType = 'created'
      const queryParams = {}
      if (props.operationType === 'duplicate') {
        notificationType = 'duplicated'
      } else if (props.operationType === 'remaining') {
        notificationType = 'remaining'
        queryParams.amount = props.prefillData?.amount || ''
      } else if (props.operationType === 'expired') {
        notificationType = 'expired'
      }

      await nextTick()

      await router.push({
        name: 'invoices-detail',
        params: { id: newId },
        query: { notification: notificationType, ...queryParams },
      })
    }
    emits('onSubmit', result.payload)
  }
}

function generateExternalId() {
  return `invoice_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`
}

function calculateInvoiceDuration(formData) {
  if (
    formData.paymentPeriod === 'custom' &&
    formData.customDate &&
    formData.customDate.length === 2
  ) {
    const now = new Date()
    const dueDate = new Date(formData.customDate[1])
    return (dueDate.getTime() - now.getTime()) * 1000000
  }

  switch (formData.paymentPeriod) {
    case '7days':
      return 7 * 24 * 60 * 60 * 1000000000
    case '14days':
      return 14 * 24 * 60 * 60 * 1000000000
    case '30days':
      return 30 * 24 * 60 * 60 * 1000000000
    default:
      return 7 * 24 * 60 * 60 * 1000000000
  }
}

watch(show, () => {
  handleBodyScroll()
})
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 9999;
  background-color: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
  overflow: hidden;
  animation: fadeIn 0.3s ease-out;
}

.modal-content {
  background: var(--bg-2, #ffffff);
  border: 1px solid var(--bg-3, #e5e7eb);
  border-radius: 10px;
  width: min(560px);
  max-height: calc(100vh - 40px);
  padding: 30px 20px 30px 30px;
  position: relative;
  overflow-y: auto;
  box-shadow: 0 0 72px rgba(3, 1, 0, 0.08);
  animation: modalSlideIn 0.3s ease-out;

  /* Анимация появления */
  transform: translateY(0) scale(1);
  opacity: 1;
}

.modal-content--mobile {
  padding: 20px;
  width: 100%;
  max-height: calc(100vh - 20px);
  border-radius: 10px;
}

.modal-close-button {
  position: absolute;
  top: 20px;
  right: 20px;
  background: none;
  border: none;
  cursor: pointer;
  color: var(--text-1, #374151);
  padding: 4px;
  border-radius: 4px;
  transition: color 0.2s ease;
  z-index: 1;
}

.modal-close-button:hover {
  color: var(--brand-hover, #2563eb);
}

.modal-close-button:focus {
  outline: none;
  color: var(--brand-hover, #2563eb);
}

.modal-close-button svg {
  width: 12px;
  height: 12px;
}

/* Анимации */
@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: translateY(-20px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

/* Адаптивность для мобильных устройств */
@media (max-width: 768px) {
  .modal-overlay {
    padding: 10px;
  }

  .modal-content {
    width: 100%;
    max-height: calc(100vh - 20px);
    padding: 20px;
  }

  .modal-close-button {
    top: 15px;
    right: 15px;
  }
}

/* Скрытие скролла для body при открытой модалке */
body.modal-open {
  overflow: hidden;
}
</style>
